import { useCallback, useEffect, useMemo, useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import { ArrowLeft, FileText, Link2, MapPin, Save, Trash2, Upload, UserRoundCheck } from "lucide-react";
import { Button, Modal } from "@fawkes/ui";

import {
  fetchWachtlijstDocument,
  openWachtlijstDocument,
  uploadWachtlijstDocument,
  verwijderWachtlijstDocument,
  type WachtlijstDocument,
} from "../api/wachtlijstDocument";
import { RelationPicker } from "../components/RelationPicker";
import { formatDate } from "../i18n/format";
import { WachtlijstDossier } from "./wachtlijst/WachtlijstDossier";
import { WachtlijstDossierKop } from "./wachtlijst/WachtlijstDossierKop";
import type { Dossier, VestigingKeuze } from "./wachtlijst/types";

/**
 * Het werkblad van de wachtlijst: links de aanvragen, rechts het dossier dat je beoordeelt.
 *
 * <p><b>Waarom de lijst blijft staan.</b> Beoordelen is doorwerken: je kijkt naar een aanvraag, belt
 * misschien, en gaat naar de volgende. Elke keer terug naar een overzicht kost je je plek in de rij
 * (mockup Maarten, 23-10-2026).
 *
 * <p>De statuskeuze staat rechtsboven bij het dossier, de interne notitie eronder, en de documenten in
 * de zijkolom — het gaat over het dier en de omstandigheden, niet over bijlagen.
 */

const STATUSSEN = [
  "NIEUW",
  "ACTIE_VEREIST",
  "IN_BEHANDELING",
  "COMPLEET",
  "GEPLAND",
  "AFGEHANDELD",
  "AFGEWEZEN",
  "INGETROKKEN",
  "VERLOPEN",
] as const;

export function WachtlijstDetail({ id, onBack }: {
  id: string;
  onBack: () => void;
  /** Een andere aanvraag openen zonder eerst terug te gaan. */
  onKies?: (aanvraagId: string) => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const [tab, setTab] = useState("overzicht");
  const { data, isLoading, isError, refetch } = useCustom<Dossier>({
    url: `wachtlijst/${id}`,
    method: "get",
  });
  const dossier = data?.data;

  const { mutate, isLoading: bezig } = useCustomMutation();
  // De koppeling met een bestaande relatie. Alleen het venster staat hier; wat er gekoppeld is komt
  // uit het dossier zelf, zodat er geen tweede waarheid ontstaat die kan gaan afwijken.
  const [koppelOpen, setKoppelOpen] = useState(false);
  const [keuzeId, setKeuzeId] = useState<number | null>(null);
  const [keuzeNaam, setKeuzeNaam] = useState<string | null>(null);
  // Het verplaatsen naar een andere locatie. De lijst komt pas als het venster opengaat: in het
  // dagelijkse werk blijft een aanvraag staan waar hij binnenkwam, en dan hoeft hij niet geladen.
  const [verplaatsOpen, setVerplaatsOpen] = useState(false);
  const [naarVestiging, setNaarVestiging] = useState<number | null>(null);
  const vestigingen = useCustom<VestigingKeuze[]>({
    url: "wachtlijst/vestigingen",
    method: "get",
    queryOptions: { enabled: verplaatsOpen },
  });
  /*
   * De foto's van de dieren, als bron-url.
   *
   * De documenten van een aanvraag zitten achter de inlog; een <img src> daarheen zou zonder token
   * gaan en dus niets laten zien. We halen ze daarom als blob op en maken er een url van, één keer
   * per document. De urls worden weer vrijgegeven zodra het dossier wisselt -- anders houdt een
   * middag doorklikken alle foto's in het geheugen vast.
   */
  const [fotoUrls, setFotoUrls] = useState<Record<string, string>>({});
  const [nieuweNotitie, setNieuweNotitie] = useState("");
  const [notitie, setNotitie] = useState("");
  const [notitieBewaard, setNotitieBewaard] = useState(true);
  const [fout, setFout] = useState<string | null>(null);

  // De notitie komt één keer mee; daarna is wat de medewerker typt leidend, ook als het dossier
  // opnieuw wordt opgehaald na een statuswijziging.
  const geladenNotitie = dossier?.interneNotitie ?? null;
  useEffect(() => {
    setNotitie(geladenNotitie ?? "");
    setNotitieBewaard(true);
  }, [geladenNotitie, id]);

  const zetStatus = (status: string) => {
    setFout(null);
    mutate(
      {
        url: `wachtlijst/${id}/status`,
        method: "put",
        values: { status },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => { void refetch(); },
        onError: () => setFout(t("wachtlijstDetail.statusFout")),
      },
    );
  };

  /**
   * De aanvraag aan een relatie hangen.
   *
   * <p>De naam op het formulier is wat de eigenaar zelf typte -- een los gegeven. Wordt de aanvraag
   * straks ingeboekt zonder dat iemand hem aan de bestaande relatie heeft gehangen, dan staat die
   * persoon een tweede keer in de administratie, met zijn dieren verdeeld over twee kaarten.
   *
   * <p>Ontkoppelen kan ook: koppelen aan de verkeerde relatie moet terug te draaien zijn, anders
   * durft niemand het.
   */
  const koppelKlant = (klantId: number | null) => {
    setFout(null);
    mutate(
      {
        url: `wachtlijst/${id}/klant`,
        method: "put",
        values: { klantId },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          setKoppelOpen(false);
          setKeuzeId(null);
          setKeuzeNaam(null);
          void refetch();
        },
        onError: () => setFout(t("wachtlijstDetail.klantFout")),
      },
    );
  };

  /**
   * De aanvraag naar een andere locatie brengen.
   *
   * <p>Hij komt binnen op de postcode van de eigenaar. Dat klopt meestal, maar niet altijd: een
   * gemeentecontract of ruimte op dat moment kan hem ergens anders doen horen. Daarom staat deze
   * keuze hier en niet in het portaal -- de eigenaar hoort niet te weten welke locaties er zijn.
   */
  const verplaats = (vestigingId: number) => {
    setFout(null);
    mutate(
      {
        url: `wachtlijst/${id}/vestiging`,
        method: "put",
        values: { vestigingId },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          setVerplaatsOpen(false);
          setNaarVestiging(null);
          void refetch();
        },
        onError: () => setFout(t("wachtlijstDetail.vestigingFout")),
      },
    );
  };

  /** Een handeling op het dossier die daarna opnieuw inleest; alle knoppen in de kop werken zo. */
  const dossierActie = (opties: { url: string; method: "post" | "put" | "delete"; values?: unknown },
                        foutTekst: string) => {
    setFout(null);
    mutate(
      {
        url: opties.url,
        method: opties.method,
        values: opties.values ?? {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => { void refetch(); },
        onError: () => setFout(foutTekst),
      },
    );
  };

  /*
   * De foto's van de dieren ophalen zodra het dossier er is.
   *
   * Alleen de documenten met soort DIERFOTO, en alleen die bij een dier horen. Ze gaan door dezelfde
   * beveiligde route als de andere documenten, dus een <img src> daarheen zou zonder token gaan en
   * niets tonen; vandaar een blob-url per foto.
   */
  const documenten = useCustom<WachtlijstDocument[]>({
    url: `wachtlijst/${id}/documenten`,
    method: "get",
  });
  const fotoKoppen = useMemo(
    () => (documenten.data?.data ?? []).filter((d) => d.soort === "DIERFOTO" && d.dierId != null),
    [documenten.data],
  );

  useEffect(() => {
    let levend = true;
    const gemaakt: string[] = [];
    void Promise.all(
      fotoKoppen
        .filter((d) => !fotoUrls[d.id])
        .map(async (d) => {
          const blob = await fetchWachtlijstDocument(id, d.id);
          const url = URL.createObjectURL(blob);
          gemaakt.push(url);
          return [d.id, url] as const;
        }),
    )
      .then((paren) => { if (levend && paren.length > 0) setFotoUrls((e) => ({ ...e, ...Object.fromEntries(paren) })); })
      // Een foto die niet laadt is geen reden om het dossier te laten struikelen; dan staat er het
      // soorticoon, zoals bij een dier zonder foto.
      .catch(() => { /* stil */ });
    return () => {
      levend = false;
      gemaakt.forEach((u) => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fotoUrls groeit hier zelf; alleen op de koppen reageren
  }, [fotoKoppen, id]);

  const fotosVan = useCallback(
    (dierId: number) => fotoKoppen
      .filter((d) => d.dierId === dierId && fotoUrls[d.id])
      .map((d) => ({ id: d.id, naam: d.bestandsnaam, src: fotoUrls[d.id] })),
    [fotoKoppen, fotoUrls],
  );

  const schrijfNotitie = () => {
    if (!nieuweNotitie.trim()) return;
    dossierActie(
      { url: `wachtlijst/${id}/notities`, method: "post", values: { tekst: nieuweNotitie.trim() } },
      t("wachtlijstDetail.notitieFout"),
    );
    setNieuweNotitie("");
  };

  const bewaarNotitie = () => {
    setFout(null);
    mutate(
      {
        url: `wachtlijst/${id}/notitie`,
        method: "put",
        values: { tekst: notitie.trim() === "" ? null : notitie },
        successNotification: false,
        errorNotification: false,
      },
      { onSuccess: () => setNotitieBewaard(true), onError: () => setFout(t("wachtlijstDetail.notitieFout")) },
    );
  };

  /*
   * Geen lijst meer naast het dossier (Maarten, 27-10-2026).
   *
   * Het dossier is breed geworden -- drie kaarten naast elkaar, dierkaarten met foto's -- en dan
   * knijpt een kolom ernaast precies datgene samen waar je naar zit te kijken. Terug naar de lijst
   * gaat met de knop erboven; dat is één klik, en die lijst heeft zelf filters en een zoekveld.
   */
  return (
    <div className="wl-werkblad wl-werkblad--breed">

      <div className="wl-werkblad__blad">
        <button type="button" className="wl-dossier__terug" onClick={onBack}>
          <ArrowLeft size={16} aria-hidden="true" /> {t("wachtlijstDossier.terugNaarLijst")}
        </button>

        {isLoading && <p className="fawkes-fin__state">{t("common.loading")}</p>}
        {isError && <p className="fawkes-error">{t("wachtlijstDetail.laadfout")}</p>}

        {dossier && (
          <>
            <WachtlijstDossier
              dossier={dossier}
              tab={tab}
              onTab={setTab}
              locale={locale}
              documentenBlok={<DocumentenBlok aanvraagId={id} locale={locale} t={t} />}
              fotosVan={fotosVan}
              bezig={bezig}
              onBeoordeel={(dierId, veldSleutel, oordeel) => dossierActie(
                { url: `wachtlijst/${id}/beoordeling`, method: "put",
                  values: { dierId, veldSleutel, oordeel } },
                t("wachtlijstDetail.beoordelingFout"),
              )}
              notitieBlok={
                <div className="wl-notitie__nieuw">
                  <textarea
                    value={nieuweNotitie}
                    onChange={(e) => setNieuweNotitie(e.target.value)}
                    placeholder={t("wachtlijstDetail.notitiePlaceholder")}
                    rows={3}
                  />
                  <Button variant="secondary" disabled={bezig || !nieuweNotitie.trim()}
                          onClick={schrijfNotitie}>
                    <Save size={15} aria-hidden="true" />
                    {t("wachtlijstDossier.notitieBewaren")}
                  </Button>
                </div>
              }
              kopBlok={
                <WachtlijstDossierKop
                  dossier={dossier}
                  locale={locale}
                  bezig={bezig}
                  onAfdrukken={() => globalThis.print()}
                  onNotitie={() => setTab("notities")}
                  onBehandelaar={(naam) => dossierActie(
                    { url: `wachtlijst/${id}/behandelaar`, method: "put", values: { naam } },
                    t("wachtlijstDetail.behandelaarFout"),
                  )}
                  onLabelErbij={(label) => dossierActie(
                    { url: `wachtlijst/${id}/labels`, method: "post", values: { label } },
                    t("wachtlijstDetail.labelFout"),
                  )}
                  onLabelWeg={(label) => dossierActie(
                    { url: `wachtlijst/${id}/labels?label=${encodeURIComponent(label)}`, method: "delete" },
                    t("wachtlijstDetail.labelFout"),
                  )}
                  statusKeuze={
                    <label className="wl-dos__statuskeuze">
                      <span>{t("wachtlijstDetail.statusZetten")}</span>
                      <select value={dossier.regel.status} disabled={bezig}
                              onChange={(e) => zetStatus(e.target.value)}>
                        {STATUSSEN.map((s) => (
                          <option key={s} value={s}>{t(`wachtlijst.status.${s}`)}</option>
                        ))}
                      </select>
                    </label>
                  }
                  vestigingBlok={
                    <>
                      <span className="wl-klant__chip">
                        <MapPin size={14} aria-hidden="true" />
                        {t("wachtlijstDetail.vestiging")}
                        <strong>{dossier.regel.vestigingNaam ?? `#${dossier.regel.vestigingId}`}</strong>
                      </span>
                      <button type="button" className="wl-klant__knop" disabled={bezig}
                              onClick={() => { setNaarVestiging(dossier.regel.vestigingId); setVerplaatsOpen(true); }}>
                        {t("wachtlijstDetail.vestigingVerplaatsen")}
                      </button>
                    </>
                  }
                  klantBlok={
                    dossier.regel.klantId != null ? (
                      <>
                        <span className="wl-klant__chip">
                          <UserRoundCheck size={14} aria-hidden="true" />
                          {t("wachtlijstDetail.klantGekoppeld")}
                          <strong>{dossier.regel.klantNaam ?? `#${dossier.regel.klantId}`}</strong>
                        </span>
                        <button type="button" className="wl-klant__knop" disabled={bezig}
                                onClick={() => koppelKlant(null)}>
                          {t("wachtlijstDetail.klantOntkoppelen")}
                        </button>
                      </>
                    ) : (
                      <button type="button" className="wl-klant__knop wl-klant__knop--hoofd" disabled={bezig}
                              onClick={() => setKoppelOpen(true)}>
                        <Link2 size={14} aria-hidden="true" />
                        {t("wachtlijstDetail.klantKoppelen")}
                      </button>
                    )
                  }
                />
              }
            />

            {/* Verplaatsen naar een andere locatie. Een keuzelijst en geen zoekveld: een organisatie
                heeft er een handvol, en je kiest er een uit wat je kent. */}
            <Modal
              open={verplaatsOpen}
              title={t("wachtlijstDetail.vestigingVerplaatsenTitel")}
              onClose={() => setVerplaatsOpen(false)}
              footer={
                <>
                  <Button variant="secondary" onClick={() => setVerplaatsOpen(false)}>
                    {t("common.cancel")}
                  </Button>
                  <Button
                    variant="primary"
                    disabled={bezig || naarVestiging == null
                      || naarVestiging === dossier.regel.vestigingId}
                    onClick={() => naarVestiging != null && verplaats(naarVestiging)}
                  >
                    {t("wachtlijstDetail.vestigingVerplaatsBevestig")}
                  </Button>
                </>
              }
            >
              <p className="fawkes-muted wl-dossier__uitleg">
                {t("wachtlijstDetail.vestigingVerplaatsenUitleg", {
                  args: [dossier.contact?.postcode ?? "—"],
                })}
              </p>
              {vestigingen.isLoading && <p className="fawkes-fin__state">{t("common.loading")}</p>}
              {!vestigingen.isLoading && (
                <label className="wl-dos__statuskeuze">
                  <span>{t("wachtlijstDetail.vestigingNaar")}</span>
                  <select
                    value={naarVestiging ?? ""}
                    onChange={(e) => setNaarVestiging(e.target.value === "" ? null : Number(e.target.value))}
                  >
                    {(vestigingen.data?.data ?? []).map((v) => (
                      <option key={v.vestigingId} value={v.vestigingId}>
                        {v.woonplaats ? `${v.naam} — ${v.woonplaats}` : v.naam}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </Modal>

            {/* Zoeken op naam, e-mail of adres -- dezelfde kiezer als de inboekwizard, zodat je hier
                niet iets anders te zien krijgt dan daar. Een nieuwe relatie aanmaken mag ook: de
                eigenaar staat er vaak nog niet in. */}
            <Modal
              open={koppelOpen}
              title={t("wachtlijstDetail.klantKoppelenTitel")}
              onClose={() => setKoppelOpen(false)}
              footer={
                <>
                  <Button variant="secondary" onClick={() => setKoppelOpen(false)}>
                    {t("common.cancel")}
                  </Button>
                  <Button variant="primary" disabled={keuzeId == null || bezig}
                          onClick={() => koppelKlant(keuzeId)}>
                    {t("wachtlijstDetail.klantKoppelBevestig")}
                  </Button>
                </>
              }
            >
              <p className="fawkes-muted wl-dossier__uitleg">
                {t("wachtlijstDetail.klantKoppelenUitleg", { args: [dossier.contact?.naam ?? "—"] })}
              </p>
              <RelationPicker
                label={t("wachtlijstDetail.klantZoek")}
                variant="step"
                stepTitle={t("wachtlijstDetail.klantZoek")}
                stepInfo={t("wachtlijstDetail.klantZoekInfo")}
                valueId={keuzeId}
                valueLabel={keuzeNaam}
                onChange={(kid, label) => { setKeuzeId(kid); setKeuzeNaam(label); }}
                canCreate
                feedPath="intake/reference/klanten"
                t={t}
              />
            </Modal>

            <section className="wl-kaart">
              <h3>{t("wachtlijstDetail.notitie")}</h3>
              <p className="fawkes-muted wl-dossier__uitleg">{t("wachtlijstDetail.notitieUitleg")}</p>
              <textarea
                className="wl-dossier__notitie"
                rows={4}
                value={notitie}
                onChange={(e) => { setNotitie(e.target.value); setNotitieBewaard(false); }}
                placeholder={t("wachtlijstDetail.notitiePlaceholder")}
              />
              <Button variant="secondary" disabled={bezig || notitieBewaard} onClick={bewaarNotitie}>
                <Save size={16} aria-hidden="true" />
                {notitieBewaard ? t("wachtlijstDetail.notitieBewaard") : t("wachtlijstDetail.notitieBewaren")}
              </Button>
            </section>

            {fout && <p className="fawkes-error" role="alert">{fout}</p>}
          </>
        )}
      </div>
    </div>
  );
}


/**
 * De bestanden bij deze aanvraag: wat de eigenaar op het platform heeft meegestuurd, plus wat het asiel
 * er zelf bij zet. De bytes staan in de backoffice; het platform bewaart ze niet.
 */
function DocumentenBlok({ aanvraagId, locale, t }: {
  aanvraagId: string;
  locale: string;
  t: (k: string, opties?: { args: string[] }) => string;
}) {
  const { data, isLoading, isError, refetch } = useCustom<WachtlijstDocument[]>({
    url: `wachtlijst/${aanvraagId}/documenten`,
    method: "get",
  });
  const lijst = data?.data ?? null;
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const haal = useCallback(() => { void refetch(); }, [refetch]);

  const kies = async (bestanden: FileList | null) => {
    if (!bestanden || bestanden.length === 0) return;
    setFout(null);
    setBezig(true);
    try {
      // Eén voor één: dan hoort een afgekeurd bestand niet de rest tegen te houden.
      for (const bestand of Array.from(bestanden)) {
        await uploadWachtlijstDocument(aanvraagId, bestand);
      }
      haal();
    } catch {
      setFout(t("wachtlijstDetail.uploadFout"));
    } finally {
      setBezig(false);
    }
  };

  const weg = async (doc: WachtlijstDocument) => {
    setFout(null);
    try {
      await verwijderWachtlijstDocument(aanvraagId, doc.id);
      haal();
    } catch {
      setFout(t("wachtlijstDetail.documentWegFout"));
    }
  };

  const bewaar = async (doc: WachtlijstDocument) => {
    const blob = await fetchWachtlijstDocument(aanvraagId, doc.id);
    const url = URL.createObjectURL(blob);
    const a = globalThis.document.createElement("a");
    a.href = url;
    a.download = doc.bestandsnaam;
    a.click();
    globalThis.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  return (
    <>
      <h3>
        {t("wachtlijstDetail.documenten")}
        {lijst && lijst.length > 0 && <span className="wl-kaart__telling">{lijst.length}</span>}
      </h3>

      {isLoading ? (
        <p className="fawkes-muted">{t("common.loading")}</p>
      ) : isError ? (
        <p className="fawkes-error">{t("wachtlijstDetail.documentenFout")}</p>
      ) : lijst == null || lijst.length === 0 ? (
        <p className="fawkes-muted">{t("wachtlijstDetail.geenDocumenten")}</p>
      ) : (
        <ul className="wl-doc">
          {lijst.map((d) => (
            <li key={d.id} className="wl-doc__regel">
              <span className="wl-doc__icoon" aria-hidden="true"><FileText size={16} /></span>
              <button type="button" className="wl-doc__naam" title={t("wachtlijstDetail.documentOpenen")}
                      onClick={() => void openWachtlijstDocument(aanvraagId, d.id)}>
                {d.bestandsnaam}
              </button>
              <span className="wl-doc__datum">{formatDate(d.aangemaaktOp, locale)}</span>
              <button type="button" className="wl-doc__knop" onClick={() => void bewaar(d)}
                      aria-label={t("wachtlijstDetail.documentBewaren")}
                      title={t("wachtlijstDetail.documentBewaren")}>
                <Save size={14} aria-hidden="true" />
              </button>
              <button type="button" className="wl-doc__knop wl-doc__knop--weg" onClick={() => void weg(d)}
                      aria-label={t("wachtlijstDetail.documentWeg")}
                      title={t("wachtlijstDetail.documentWeg")}>
                <Trash2 size={14} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <label className="wl-doc__toevoegen">
        <input
          type="file"
          multiple
          accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif"
          disabled={bezig}
          onChange={(e) => { void kies(e.target.files); e.target.value = ""; }}
        />
        <span className="wl-doc__toevoegen-knop">
          <Upload size={15} aria-hidden="true" />
          {bezig ? t("wachtlijstDetail.uploadBezig") : t("wachtlijstDetail.documentToevoegen")}
        </span>
      </label>

      {fout && <p className="fawkes-error" role="alert">{fout}</p>}
    </>
  );
}
